import { ObjectId } from "bson";
import { Marshalled, Unmarshalled } from "../types";
import { toDateUtcString } from "./dateUtils";

export const capitalize = (text?: string): string =>
    text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

// tslint:disable-next-line:no-tautology-expression
export const isReallyNaN = (value: any) => value !== value;

export const stringToNumber = (value: string | undefined | null): number | null => {
    if (value === null || value === undefined || value.length === 0) {
        return null;
    }

    const output = Number(value);

    return isReallyNaN(output) ? null : output;
};

export const capitalizeWords = (text: string): string =>
    text ? text.replace(/(?:^|\s)\S/g, (a) => a.toUpperCase()) : "";

export const studly = (text: string): string => capitalizeWords(text.replace(/_/g, " "));

export const isTrue = (text: string): boolean => String(text).toLowerCase() === "true";

export const randomString = (length: number = 6): string => {
    const possible = "abcdefghijklmnopqrstuvwxyz0123456789";
    let text = "";

    for (let i = 0; i < length; i += 1) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
    }

    return text;
};

export const randomNumber = (length: number = 6): string => {
    const possible = "0123456789";
    let text = "";

    for (let i = 0; i < length; i += 1) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
    }

    return text;
};

export const enumIncudes = (enum_: any, value: any): boolean =>
    Object.values(enum_).includes(value);

export const mapToEnum = <E>(enum_: any, value: any, defaultValue: E): E =>
    enumIncudes(enum_, value) ? value : defaultValue;

export const sleep = async (time: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, time));

export const marshal = <T>(value: T): Marshalled<T> => {
    if (value === null || value === undefined) {
        return undefined as any;
    }

    if (typeof value === "string" && value === "") {
        return undefined as any;
    }

    if (Array.isArray(value)) {
        return value.map(marshal) as any;
    }

    if (value instanceof Date) {
        return toDateUtcString(value) as any;
    }

    // ObjectId does not exist on frontend
    if (ObjectId && value instanceof ObjectId) {
        return value.toHexString() as any;
    }

    if (isObject(value)) {
        return objectFromEntries(
            Object.entries(value).map(([key, keyValue]) => [key, marshal(keyValue)]),
        );
    }

    return value;
};

export const unmarshal = <T>(value: T): Unmarshalled<T> => {
    if (value === null || value === undefined) {
        return null as any;
    }

    if (Array.isArray(value)) {
        return value.map(unmarshal) as any;
    }

    if (value instanceof Date) {
        return value as any;
    }

    if (ObjectId && value instanceof ObjectId) {
        return value as any;
    }

    if (isObject(value)) {
        return objectFromEntries(
            Object.entries(value).map(([key, keyValue]) => [key, unmarshal(keyValue)]),
        );
    }

    return value;
};

export const isObject = (a: any): a is Record<any, any> => a === Object(a);

export const objectFromEntries = (entries: any): any => {
    const output: any = {};

    for (const [key, value] of entries) {
        output[key] = value;
    }

    return output;
};
